import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { IMAGES } from "../data";

const LINES = [
  { text: "LEADING. UNITING.", italic: false },
  { text: "STRETCHING HORIZONS.", italic: true },
];

const MaskedLine = ({ text, italic, delay }) => (
  <div className="overflow-hidden py-1">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`font-display font-semibold leading-[0.95] tracking-tight text-cream text-[10.5vw] md:text-[6.6vw] lg:text-[4.1vw] 2xl:text-[4.8vw] ${
        italic ? "italic text-amber" : ""
      }`}
    >
      {text}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[calc(100svh-76px)] min-h-[580px] overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Wooden chalets glowing at dusk inside the LUSH Adventure forest estate"
          className="w-full h-full object-cover"
          data-testid="hero-background-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-night/70 via-night/30 to-forest" />
      <div className="absolute inset-0 bg-black/25" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-between pt-20 md:pt-24 pb-8 md:pb-8"
      >
        <div className="overflow-hidden shrink-0">
          <motion.div
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src="/logo-light.png"
              alt="LUSH Adventure @ Barn — Leading. Uniting. Stretching Horizons."
              data-testid="hero-logo"
              className="h-24 md:h-28 lg:h-32 2xl:h-36 w-auto drop-shadow-[0_4px_24px_rgba(4,15,11,0.8)]"
            />
          </motion.div>
        </div>

        <div className="shrink-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex items-center gap-3 mb-6"
          data-testid="hero-location-label"
        >
          <MapPin size={14} className="text-amber" />
          <span className="text-xs tracking-[0.3em] uppercase text-cream/80">
            Kiriberiya · Panadura · Sri Lanka
          </span>
        </motion.div>

        <h1 data-testid="hero-headline">
          {LINES.map((l, i) => (
            <MaskedLine key={l.text} text={l.text} italic={l.italic} delay={0.7 + i * 0.15} />
          ))}
        </h1>

        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-lg text-sm lg:text-base leading-relaxed text-cream/80"
          >
            A purpose-designed experience center where scientific precision meets cinematic
            immersion. At Barn House — an award-winning sustainable outdoor venue — with LMST
            equips individuals and teams to lead boldly, unite fiercely, and stretch beyond
            limits to achieve lasting success.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 2.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() => document.getElementById("experiences")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-amber text-night text-xs tracking-[0.2em] uppercase font-semibold px-8 py-4 hover:bg-cream transition-colors duration-300"
            >
              Explore the Zones
            </button>
            <button
              data-testid="hero-cta-book"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="border border-cream/40 text-cream text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-amber hover:text-amber transition-colors duration-300"
            >
              Book an Adventure
            </button>
          </motion.div>
        </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/60"
        data-testid="hero-scroll-indicator"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};
